import throttle from 'lodash/throttle';
import type plyr from 'plyr';

import { getRequestType } from '@lyearn/lygo-core';

import { playerContainerHover } from './Video/videoStyles';
import PlayerProps from './types';

const OVERLAY_TOGGLE_TIMEOUT = 100;

export const addPlyrEventListeners = (
  setMaxProgressCallback: (newProgress: number) => void,
  plyrInstance?: any,
  onProgress?: (attrs: { time: number }) => void,
  onLoad?: (player: plyr) => void,
  onError?: (player: plyr) => void,
  provider?: PlayerProps['provider'],
  onShowOverlay?: () => void,
) => {
  if (onLoad) {
    plyrInstance?.on('loadedmetadata', (event: any) => {
      onLoad?.(event.detail.plyr);
    });

    plyrInstance.once('ready', () => {
      const videoElement = plyrInstance?.elements?.container?.querySelector('video');
      if (videoElement && videoElement.readyState > 0) {
        videoElement.load(); // Manually load the video metadata in ios devices that will trigger onLoad function
      }
    });
  }

  if (onError) {
    plyrInstance?.on('stalled', (event: any) => {
      onError?.(event.detail.plyr);
    });
  }

  if (onProgress) {
    plyrInstance?.on(
      'timeupdate',
      throttle((event: any) => {
        const player = event.detail.plyr;
        if (player && player.media) {
          const currentTime = player.currentTime;
          if (currentTime) {
            setMaxProgressCallback((currentTime / player.duration) * 100);
            onProgress({ time: currentTime });
          }
        }
      }, 2000),
    );
  }

  if (provider === 'youtube' && onLoad) {
    plyrInstance?.on('ready', (event: any) => {
      onLoad?.(event.detail.plyr);
    });
  }

  if (provider === 'vimeo' && onLoad) {
    plyrInstance?.on('durationchange', (event: any) => {
      onLoad?.(event.detail.plyr);
    });
  }

  plyrInstance?.on('play', (event: any) => {
    const videoElements = document.querySelectorAll('video');
    if (videoElements.length === 1) {
      return;
    }
    const currentVideoElement = event.srcElement.querySelector('video');
    for (const videoElement of videoElements) {
      if (videoElement !== currentVideoElement) {
        videoElement.pause();
      }
    }
  });

  if (onShowOverlay) {
    plyrInstance?.on('ended', (event: any) => {
      setTimeout(() => onShowOverlay(), OVERLAY_TOGGLE_TIMEOUT);
    });
  }

  // Event listener for entering fullscreen
  plyrInstance.on('enterfullscreen', (event: any) => {
    try {
      if (event?.detail?.plyr?.type === 'video' && !isVideoPortrait(event?.detail?.plyr?.ratio)) {
        // @ts-ignore
        if (screen.orientation && screen.orientation.lock) {
          try {
            // @ts-ignore
            screen.orientation.lock('landscape').catch((error) => {
              console.error('Promise rejection: Error locking orientation:', error);
            });
          } catch (error) {
            console.error('Synchronous error locking orientation:', error);
          }
        }

        // @ts-ignore
        if (window.AndroidWebAppInterface && window.AndroidWebAppInterface.enterFullscreen) {
          try {
            // @ts-ignore
            window.AndroidWebAppInterface.enterFullscreen();
          } catch (error) {
            console.error('Error entering fullscreen:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error entering fullscreen:', error);
    }
  });

  // Event listener for exiting fullscreen
  plyrInstance.on('exitfullscreen', () => {
    try {
      // @ts-ignore
      if (screen.orientation && screen.orientation.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (error) {
          console.error('Error unlocking orientation:', error);
        }
      }

      // @ts-ignore
      if (window.AndroidWebAppInterface && window.AndroidWebAppInterface.exitFullscreen) {
        try {
          // @ts-ignore
          window.AndroidWebAppInterface.exitFullscreen();
        } catch (e) {
          console.error('Error exiting fullscreen:', e);
        }
      }
    } catch (error) {
      console.error('Error exiting fullscreen:', error);
    }
  });
};

export const getTargetTime = (input: any) => {
  if (typeof input === 'object' && (input.type === 'input' || input.type === 'change')) {
    return (input.target.value / input.target.max) * 100;
  } else {
    // We're assuming its a number
    return Number(input);
  }
};

export const isShakaSupported = (url?: string) => {
  return url ? ['.mpd', '.m3u8'].includes(url.slice(url.lastIndexOf('.'))) : false;
};

export const isVideoProcessing = (
  requestUrl: string,
  setProcessingProgress: React.Dispatch<React.SetStateAction<number | undefined>>,
  requestFunction?: getRequestType,
) => {
  return new Promise((resolve, _reject) => {
    const isShakaVideoProcessing = () => {
      requestFunction?.(requestUrl).then(
        (res: { data: { status: { state: string; pctComplete?: string } } }) => {
          const processingProgress = res.data.status?.pctComplete
            ? Math.round(parseFloat(res.data.status?.pctComplete))
            : 0;
          if (res.data.status.state === 'ready') {
            setProcessingProgress(undefined);
            resolve(true);
          } else {
            setProcessingProgress(processingProgress);
            setTimeout(isShakaVideoProcessing, 5000);
          }
        },
      );
    };
    isShakaVideoProcessing();
  });
};

export const setPlayerForward = (player: any, progress: number) => {
  player?.forward(progress === 100 ? 0 : player.duration * (progress / 100));
};

let mouseMoveTimeoutId = null as any;
let touchEndTimeoutId = null as any;
export const mouseOverEventListener = (event: React.MouseEvent<HTMLDivElement>) => {
  const plyrContainer = event.currentTarget.closest('.plyr-container');
  const isPlyrControls =
    (event.target as Element).closest('.plyr__controls') ||
    (event.target as Element).closest('.plyr__control--overlaid');
  plyrContainer?.classList.add(playerContainerHover);
  clearTimeout(mouseMoveTimeoutId);
  if (!isPlyrControls) {
    mouseMoveTimeoutId = setTimeout(() => {
      plyrContainer?.classList.remove(playerContainerHover);
    }, 2000);
  }
};

export const mouseOutEventListener = (event: React.MouseEvent<HTMLDivElement>) => {
  const plyrContainer = event.currentTarget.closest('.plyr-container');
  plyrContainer?.classList.remove(playerContainerHover);
};

export const touchEndEventListener = (event: React.TouchEvent<HTMLDivElement>) => {
  const plyrContainer = event.currentTarget.closest('.plyr-container');
  clearTimeout(touchEndTimeoutId);
  touchEndTimeoutId = setTimeout(() => {
    plyrContainer?.classList.remove(playerContainerHover);
  }, 2000);
};
// Ref: https://github.com/cookpete/react-player/blob/master/src/patterns.js
const MATCH_URL_YOUTUBE =
  /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=|shorts\/))((\w|-){11})|youtube\.com\/playlist\?list=|youtube\.com\/user\//;
const MATCH_URL_VIMEO = /vimeo\.com\/(?!progressive_redirect).+/;
const AUDIO_EXTENSIONS = /\.(m4a|m4b|mp4a|mpga|mp2|mp2a|mp3|m2a|m3a|wav|weba|aac|oga|spx)($|\?)/i;
const VIDEO_EXTENSIONS = /\.(mp4|og[gv]|webm|mov|m4v)(#t=[,\d+]+)?($|\?)/i;
const HLS_EXTENSIONS = /\.(m3u8)($|\?)/i;
const DASH_EXTENSIONS = /\.(mpd)($|\?)/i;
const FLV_EXTENSIONS = /\.(flv)($|\?)/i;

export const canPlayVideo = (url: string) => {
  return (
    VIDEO_EXTENSIONS.test(url) ||
    MATCH_URL_YOUTUBE.test(url) ||
    MATCH_URL_VIMEO.test(url) ||
    HLS_EXTENSIONS.test(url) ||
    DASH_EXTENSIONS.test(url) ||
    FLV_EXTENSIONS.test(url)
  );
};

export const canPlayAudio = (url: string) => AUDIO_EXTENSIONS.test(url);

const YOUTUBE_URL_REGEX =
  /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|watch\/|watch\?v=|watch\?.+&v=))((\w|-){11})/;

export const getYoutubeVideoId = (url: string) => url.match(YOUTUBE_URL_REGEX)?.[1];

// Ref: https://stackoverflow.com/a/41209591
const VIMEO_URL_REGEX =
  /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;

export const getVimeoVideoId = (url: string) => url.match(VIMEO_URL_REGEX)?.[1];

export const getVideoProviderDetails = (
  url: string,
): { provider: PlayerProps['provider']; videoId?: string } => {
  const youtubeVideoId = getYoutubeVideoId(url);
  if (youtubeVideoId) {
    return {
      provider: 'youtube',
      videoId: youtubeVideoId,
    };
  }

  const vimeoVideoId = getVimeoVideoId(url);
  if (vimeoVideoId) {
    return {
      provider: 'vimeo',
      videoId: vimeoVideoId,
    };
  }

  return { provider: 'html5' };
};

// Ref: https://www.regextester.com/97125
const AWS_S3_URL_REGEX = /(s3-|s3\.)?(.*)\.amazonaws\.com/;
const CDN_URL_REGEX = /files\.lyearn\.com/;

export const isAwsS3Url = (url: string) => AWS_S3_URL_REGEX.test(url);
export const isCdnUrl = (url: string) => CDN_URL_REGEX.test(url);
export const videoCrossOrigin = (url: string) => {
  if (isAwsS3Url(url) || isCdnUrl(url)) return undefined;
  return 'anonymous';
};
const isVideoPortrait = (ratio: string) => {
  if (!ratio) {
    return false;
  }

  const tokens = ratio.split(':');
  if (tokens.length !== 2) {
    return false;
  }

  try {
    const width = parseInt(tokens[0]);
    const height = parseInt(tokens[1]);

    return width <= height;
  } catch (e) {
    console.error('Unable to parse aspect ratio: ', ratio);
    return false;
  }
};

export const getVideoIdFromSrc = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const tokens = urlObj.pathname.split('/');
    return tokens.length >= 2 ? tokens[1] : '';
  } catch (err) {
    return '';
  }
};
